// ── Unique-value validation config (pure logic) ───────────────────────────────
//
// The admin authors "this field must be unique" from Form Groups → Field →
// Validations. It is stored as an ordinary entry in the field's existing
// `validations` array, so nothing about persistence, reordering or duplicating
// fields had to change:
//
//   { type: 'unique', message: 'Email already present',
//     value: { collectionField: 'email', normalizer: 'email',
//              skipBlank: true, validateOn: 'blur' } }
//
// The rule's `value` is an OBJECT rather than a scalar. That works because
// buildFieldPayload only coerces `rule.value` with Number() when the validation
// type declares `valueType: 'number'` — an object valueType therefore rides
// through a save untouched — and initFieldValues loads it back with
// `v.value ?? ''`, which passes objects through unchanged.
//
// This module holds every piece of logic that does not need React, because
// FormGroupsSection.jsx imports AddFormV1 → react-pdf, which cannot be imported
// under jsdom (no DOMMatrix). Keeping the rules here is what makes them
// testable at all.
//
// NOTHING here knows about any particular module, collection or field name.

export const UNIQUE_VALIDATION_TYPE = 'unique';

// Normalizers must match the backend's `models/uniqueValidation.go` names
// exactly — the admin picks the token, the server implements it. A value the
// server does not recognise falls back to `exact` there, so an unknown token
// can never widen a uniqueness check into "no check".
export const UNIQUE_NORMALIZERS = [
  { label: 'Exact (compare as stored)', value: 'exact' },
  { label: 'Trim whitespace',           value: 'trim' },
  { label: 'Trim + lowercase',          value: 'trimLower' },
  { label: 'Email (trim + lowercase)',  value: 'email' },
  { label: 'Digits only (phone/ID)',    value: 'digitsOnly' },
];

// Blur is the recommended trigger: it gives the admin's error message the best
// chance of being seen next to the field the user just typed in. "Submit only"
// exists for fields where a per-blur round trip is unwanted. Submit-time
// enforcement happens for BOTH — the trigger only controls the preflight probe.
export const UNIQUE_VALIDATION_TRIGGERS = [
  { label: 'On blur (recommended)', value: 'blur' },
  { label: 'On submit only',        value: 'submit' },
];

// Defaults deliberately mirror what the RUNTIME falls back to when a stored
// config omits the key (components/form/uniqueFieldValidation.js): 'trim' and
// 'blur'. If the editor defaulted to anything else it would DISPLAY a setting
// the form does not actually apply, and then bake that lie in on the next save.
export const DEFAULT_UNIQUE_NORMALIZER = 'trim';
export const DEFAULT_UNIQUE_TRIGGER = 'blur';

// Triggers the editor OFFERS are blur and submit. The set it ACCEPTS is wider:
// the runtime also understands 'change', so a config carrying it must survive a
// save from this screen untouched rather than being quietly downgraded to blur.
const ACCEPTED_TRIGGERS = new Set(['blur', 'submit', 'change']);

// v1 compares a single scalar against a single stored scalar. Anything holding
// a list (or rich markup, whose "equality" is meaningless to a user) is out of
// scope until the backend grows an element-wise comparison, and the editor must
// say so rather than saving a rule that would silently never match.
const UNSUPPORTED_FIELD_TYPES = new Set(['file', 'text-editor', 'checkbox']);

export const UNIQUE_SCALAR_ONLY_HINT = 'Uniqueness currently supports scalar values only —'
  + ' a single value compared against a single stored value.';

// uniqueFieldTypeSupport reports whether a unique rule may be configured on the
// field currently being edited, and WHY not when it may not. `field` is the raw
// field-editor row (post initFieldValues), not the saved document.
export function uniqueFieldTypeSupport(field = {}) {
  const type = String(field.type ?? 'text');
  const deny = (reason) => ({ supported: false, reason: `${reason} ${UNIQUE_SCALAR_ONLY_HINT}` });

  if (UNSUPPORTED_FIELD_TYPES.has(type)) {
    const label = type === 'text-editor' ? 'Rich text' : type === 'file' ? 'File' : 'Checkbox';
    return deny(`${label} fields cannot be checked for uniqueness.`);
  }
  if (type === 'tagify') return deny('Tag fields store a list of values.');
  if (field.multiSelect) return deny('Multi-select fields store a list of values.');
  if (field.multiple) return deny('Multi-value fields store a list of values.');
  if (field.addRow) return deny('Repeatable fields store one value per row.');
  if (Array.isArray(field.subFields) && field.subFields.length) {
    return deny('Fields with sub-fields store one object per row.');
  }
  if (field.dataType === 'array') return deny('Fields sent as an array store a list of values.');
  return { supported: true, reason: '' };
}

// ── Stored-path safety ────────────────────────────────────────────────────────
//
// `collectionField` becomes a Mongo query key server-side. A dotted path is
// legitimate and useful (`passport.passportNumber`), but `$`-prefixed segments
// would be read as operators, a NUL byte can truncate a key, and an empty
// segment (`a..b`, `.b`, `a.`) is not a path at all. Reject all three here so a
// malformed path is never written to config in the first place — the backend
// validates independently, this is only the earliest, clearest failure point.
const SEGMENT_RE = /^[A-Za-z_][A-Za-z0-9_-]*$/;

export function collectionFieldPathError(path) {
  const raw = String(path ?? '');
  if (!raw.trim()) return 'Collection field is required';
  if (raw !== raw.trim()) return 'Remove the surrounding spaces';
  if (raw.includes('\0')) return 'Path contains a null byte';
  if (raw.includes('$')) return 'Path may not contain "$"';
  const segments = raw.split('.');
  for (const segment of segments) {
    if (!segment) return 'Path has an empty segment — use a.b, not a..b';
    if (!SEGMENT_RE.test(segment)) {
      return `Invalid path segment "${segment}" — letters, digits, _ and - only`;
    }
  }
  return '';
}

export function isSafeCollectionFieldPath(path) {
  return collectionFieldPathError(path) === '';
}

// ── Config shaping ────────────────────────────────────────────────────────────

// defaultCollectionField mirrors the backend's resolution order for "which key
// does this form field actually land on in the document": payloadKey wins when
// present (it IS the stored key), otherwise the field key.
export function defaultCollectionField(field = {}) {
  const payloadKey = String(field.payloadKey ?? '').trim();
  if (payloadKey) return payloadKey;
  return String(field.field ?? '').trim();
}

export function defaultUniqueConfig(field = {}) {
  return {
    collectionField: defaultCollectionField(field),
    normalizer: DEFAULT_UNIQUE_NORMALIZER,
    // Blank optional values must never conflict with one another — two records
    // that both left the field empty are not duplicates. Default ON.
    skipBlank: true,
    validateOn: DEFAULT_UNIQUE_TRIGGER,
  };
}

const KNOWN_NORMALIZERS = new Set(UNIQUE_NORMALIZERS.map((n) => n.value));

// normalizeUniqueConfig fills in the defaults for a stored (or half-typed)
// config WITHOUT dropping anything it does not recognise. Unknown keys are
// preserved verbatim so a config seeded by a backend migration — or written by
// a newer version of this screen — survives an unrelated save by an older
// client. That property is what makes load → edit → reorder → duplicate → save
// lossless.
export function normalizeUniqueConfig(raw, field = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const defaults = defaultUniqueConfig(field);
  const collectionField = String(source.collectionField ?? '').trim() || defaults.collectionField;
  const normalizer = KNOWN_NORMALIZERS.has(source.normalizer) ? source.normalizer : defaults.normalizer;
  const validateOn = ACCEPTED_TRIGGERS.has(source.validateOn) ? source.validateOn : defaults.validateOn;
  // skipBlank is a genuine tri-state on read: absent means "never configured",
  // which takes the safe default (true). An explicit `false` must survive —
  // this is the same trap the Go side guards with a *bool.
  const skipBlank = source.skipBlank === undefined || source.skipBlank === null
    ? defaults.skipBlank
    : Boolean(source.skipBlank);
  return { ...source, collectionField, normalizer, skipBlank, validateOn };
}

// uniqueRuleErrors returns every reason the given rule may not be saved.
// Returns [] when the rule is valid — or when it is not a unique rule at all.
export function uniqueRuleErrors(rule, field = {}) {
  if (!rule || rule.type !== UNIQUE_VALIDATION_TYPE) return [];
  const errors = [];
  const support = uniqueFieldTypeSupport(field);
  if (!support.supported) errors.push(support.reason);
  if (!String(rule.message ?? '').trim()) {
    errors.push('An error message is required — it is what the user sees.');
  }
  const config = rule.value && typeof rule.value === 'object' ? rule.value : {};
  const pathError = collectionFieldPathError(config.collectionField);
  if (pathError) errors.push(pathError);
  return errors;
}

export function isUniqueRule(rule) {
  return Boolean(rule) && rule.type === UNIQUE_VALIDATION_TYPE;
}

// hasUniqueValidation answers "does this SAVED field carry a unique rule" — used
// by Detail Groups to render a read-only tag. It must never mutate anything.
export function hasUniqueValidation(field) {
  return (field?.validations ?? []).some(isUniqueRule);
}

export function uniqueValidationOf(field) {
  return (field?.validations ?? []).find(isUniqueRule) ?? null;
}

// ── Resolved target ───────────────────────────────────────────────────────────
//
// The admin never types a collection name. The target MODULE is the group's
// Target Collection (`group.moduleName`) when set — the fields of such a group
// are written to that module through moduleWrites, so its collection, not the
// primary module's, is where the value must be unique — otherwise the primary
// module the group belongs to. The COLLECTION is then looked up from module
// configuration (adminApi's module registry), never derived from user input.
export function resolveUniqueTarget({
  primaryModule = '',
  groupTargetModule = '',
  field = {},
  config = {},
  collectionForModule,
} = {}) {
  const module = String(groupTargetModule ?? '').trim() || String(primaryModule ?? '').trim();
  const collection = typeof collectionForModule === 'function'
    ? String(collectionForModule(module) ?? '').trim()
    : '';
  const formField = String(field.field ?? '').trim();
  const storedField = String(config.collectionField ?? '').trim() || defaultCollectionField(field);
  return {
    module,
    // Blank collection = the registry has not been loaded (or the module is not
    // registered). The UI shows an explicit "resolved on the server" note in
    // that case rather than inventing a name.
    collection,
    formField,
    storedField,
    // True when the group writes somewhere other than the module being edited,
    // which is worth calling out in the UI — it is the surprising case.
    isCrossModule: Boolean(String(groupTargetModule ?? '').trim())
      && String(groupTargetModule).trim() !== String(primaryModule ?? '').trim(),
  };
}
